import React, { useState } from 'react';
import { isInWishlist, toggleWishlist } from '../utils/wishlist';
import { trackAction } from '../utils/achievements';
import { API_BASE } from '../utils/api';
import { authHeaders } from '../utils/auth';

function RouteList({ routes, onVote, onRouteClick, userVotes, onDiscussClick }) {
  const STATUS_CONFIG = {
    proposed:  { label: 'Proposed',  emoji: '🟡', className: 'status-proposed' },
    trending:  { label: 'Trending',  emoji: '🔥', className: 'status-trending' },
    announced: { label: 'Announced', emoji: '📢', className: 'status-announced' },
    launched:  { label: 'Launched',  emoji: '✅', className: 'status-launched' },
    rejected:  { label: 'Rejected',  emoji: '❌', className: 'status-rejected' },
  };

  const [votingRouteId, setVotingRouteId] = useState(null);
  const [wishlistState, setWishlistState] = useState({});
  const [copiedId, setCopiedId] = useState(null);

  const handleVote = async (routeId, voteType) => {
    setVotingRouteId(routeId);

    try {
      const response = await fetch(`${API_BASE}/routes/${routeId}/vote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders()
        },
        body: JSON.stringify({ vote_type: voteType })
      });

      if (!response.ok) {
        throw new Error('Failed to vote');
      }

      const updatedRoute = await response.json();
      
      // Track achievement
      trackAction('vote_given');
      
      if (onVote) {
        onVote(updatedRoute);
      }
    } catch (err) {
      console.error('Error voting:', err);
    } finally {
      setVotingRouteId(null);
    }
  };

  const handleToggleWishlist = (routeId) => {
    toggleWishlist(routeId);
    setWishlistState({ ...wishlistState, [routeId]: !wishlistState[routeId] });
  };

  const handleShare = (route) => {
    const text = `${route.origin} → ${route.destination} needs to happen — ${route.vote_count || 0} votes on AeroSuggest!`;
    const url = 'https://aerosuggest.com';
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      '_blank',
      'noopener,noreferrer'
    );
    navigator.clipboard?.writeText(url).catch(() => {});
    setCopiedId(route.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getUserVote = (routeId) => {
    return userVotes?.[routeId] || null;
  };

  if (!routes || routes.length === 0) {
    return (
      <div className="empty-state">
        <p>No routes suggested yet.</p>
        <p>Be the first to suggest a route!</p>
      </div>
    );
  }

  return (
    <div className="routes-list">
      {routes.map(route => {
        const voteCount = route.vote_count || 0;
        const commentCount = route.comment_count || 0;
        const userVote = getUserVote(route.id);
        const isVoting = votingRouteId === route.id;
        const inWishlist = isInWishlist(route.id);
        const analysis = route.analysis || {};

        return (
          <div 
            key={route.id} 
            className="route-card"
          >
            <div className="route-header">
              {route.status && STATUS_CONFIG[route.status] && (
                <div className={`status-badge ${STATUS_CONFIG[route.status].className}`}>
                  {STATUS_CONFIG[route.status].emoji} {STATUS_CONFIG[route.status].label}
                  {route.status_note && (
                    <span className="status-note" title={route.status_note}> ℹ</span>
                  )}
                </div>
              )}
              <div className="route-path" onClick={() => onRouteClick && onRouteClick(route)} style={{ cursor: 'pointer' }}>
                <span>{route.origin}</span>
                <span className="route-arrow">→</span>
                <span>{route.destination}</span>
              </div>
              <div className="route-actions-top">
                <button
                  className={`wishlist-btn ${inWishlist ? 'active' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleWishlist(route.id);
                  }}
                  title={inWishlist ? "Remove from wishlist" : "Add to wishlist"}
                >
                  {inWishlist ? '⭐' : '☆'}
                </button>
                <button
                  className={`share-btn ${copiedId === route.id ? 'copied' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleShare(route);
                  }}
                  title="Share this route"
                  aria-label="Share this route on Twitter"
                >
                  {copiedId === route.id ? '✓' : '↗'}
                </button>
                <div className={`vote-display ${voteCount > 0 ? 'positive' : voteCount < 0 ? 'negative' : 'neutral'}`}>
                  {voteCount > 0 ? '+' : ''}{voteCount}
                </div>
              </div>
            </div>

            <div className="route-info">
              {route.origin_airport && (
                <div className="airport-name">
                  <span className="code">{route.origin_airport.iata}</span>
                  <span>{route.origin_airport.name}</span>
                </div>
              )}
              {route.destination_airport && (
                <div className="airport-name">
                  <span className="code">{route.destination_airport.iata}</span>
                  <span>{route.destination_airport.name}</span>
                </div>
              )}
            </div>

            {/* Route Analysis */}
            {analysis.distance_km && (
              <div className="route-analysis">
                <div className="analysis-row">
                  <span className="analysis-label">Distance:</span>
                  <span className="analysis-value">{analysis.distance_km.toLocaleString()} km ({analysis.distance_miles?.toLocaleString()} mi)</span>
                </div>
                <div className="analysis-row">
                  <span className="analysis-label">Category:</span>
                  <span className="analysis-value">{analysis.distance_category}</span>
                </div>
                <div className="analysis-row">
                  <span className="analysis-label">Demand:</span>
                  <span className="analysis-value">{analysis.demand_signal}</span>
                </div>
              </div>
            )}

            {/* Suggested Airlines */}
            {route.suggested_airlines && (
              <div className="suggested-airlines">
                <span className="airlines-label">Suggested for:</span>
                <span className="airlines-value">{route.suggested_airlines}</span>
              </div>
            )}

            <div className="vote-buttons" onClick={(e) => e.stopPropagation()}>
              <button
                className={`btn-vote upvote ${userVote === 1 ? 'active' : ''}`}
                onClick={() => handleVote(route.id, 1)}
                disabled={isVoting}
              >
                ▲ Upvote
              </button>
              <button
                className={`btn-vote downvote ${userVote === -1 ? 'active' : ''}`}
                onClick={() => handleVote(route.id, -1)}
                disabled={isVoting}
              >
                ▼ Downvote
              </button>
            </div>

            <div className="route-footer">
              <div className={`comment-count-badge ${commentCount > 0 ? 'has-comments' : ''}`}>
                💬 {commentCount} {commentCount === 1 ? 'comment' : 'comments'}
              </div>
              <button 
                className="discuss-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onDiscussClick && onDiscussClick(route);
                }}
              >
                Discuss
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default RouteList;
